import { TFunction } from "i18next";
import React, { ReactNode } from "react";
import { ColumnOptionsPanel } from "./ColumnOptionsPanel";
import { Column, Filter, FilterProps, LocalView, SettingButtonsDisable, SectionStyle, Taxonomy } from "./types";

export type IDatePickerStrings = {
  months: string[];
  shortMonths: string[];
  days: string[];
  shortDays: string[];
  goToToday: string;
  prevMonthAriaLabel?: string;
  nextMonthAriaLabel?: string;
  prevYearAriaLabel?: string;
  nextYearAriaLabel?: string;
  closeButtonAriaLabel?: string;
};

export type Strings = {
  headerLabel?: string;
  applyButtonLabel?: string;
  resetButtonLabel?: string;
  newViewButtonLabel?: string;
  editViewButtonLabel?: string;
  deleteViewButtonLabel?: string;
  clearFiltersButtonLabel?: string;
  saveViewButtonLabel?: string;
  visibilityCheckboxTitle?: string;
  visibilitySelectAllTitle?: string;
  visibilityDeselectAllTitle?: string;
  dragHandleTitle?: string;
  clearFilterButtonLabel?: string;
  dateFilterOperatorIsAfterLabel?: string;
  dateFilterOperatorIsBeforeLabel?: string;
  dateFilterOperatorIsExactLabel?: string;
  dateFilterOperatorIsBetweenLabel?: string;
  numberFilterOperatorGtLabel?: string;
  numberFilterOperatorLtLabel?: string;
  numberFilterOperatorEqLabel?: string;
  numberFilterOperatorBetweenLabel?: string;
  numberFilterIncludeLabel?: string;
  numberFilterAndLabel?: string;
  booleanFilterTrueLabel?: string;
  booleanFilterFalseLabel?: string;
  textFilterLabel?: string;
  createViewDialogLabel?: string;
  editViewDialogLabel?: string;
  viewDialogSaveButtonLabel?: string;
  requiredErrorMessage?: string;
  viewExistsErrorMessage?: string;
  viewDialogNameFieldLabel?: string;
  viewDialogDescriptionFieldLabel?: string;
  viewDialogFavoriteFieldLabel?: string;
  viewSelectNoViewsLabel?: string;
  viewSelectPlaceholder?: string;
  viewSelectLoadingLabel?: string;
  resetAllStorageTitle?: string;
  resetAllStorageMessage?: string;
  resetAllStorageConfirm?: string;
  resetAllStorageCancel?: string;
  resetAllStorageTooltip?: string;
  datePickerStrings?: IDatePickerStrings;
};

export type ColumnOptionsProps = {
  id?: string;
  closePanel: () => void;
  columns: Column[];
  onClickApply: () => void;
  views: LocalView[];
  selectedView: LocalView | null;
  onChangeView: (viewId: string | null) => void;
  isReady: boolean;
  isViewLoading: boolean;
  setFilter: (columnKey: string, filter: Filter) => void;
  maxSelection?: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  taxonomy: { [key: string]: Taxonomy[] };
  onClickDeleteView: () => void;
  onClickClearAllFilters: () => void;
  onClickReset: () => void;
  onClickSaveView: () => void;
  onCreateView: (data: {
    name: string;
    description: string;
    isDefault: boolean;
  }) => void;
  onUpdateView: (data: {
    name: string;
    description: string;
    isDefault: boolean;
  }) => void;
  onReorderColumn: (sourceIndex: number, destinationIndex: number) => void;
  onToggleColumnVisibility: (columnKey: string) => void;
  onResetAllStorage?: () => void;
  renderAdditionalOptions?: () => ReactNode;
  additionalOptionsHeight?: number;
  additionalOptionsStyle?: SectionStyle;
  strings?: Strings;
  t: TFunction;
  hideViewOptionsFeatures?: boolean;
  disableButtons?: SettingButtonsDisable;
  filterProps?: FilterProps;
  usePlaceholder?: boolean;
  disableNewView?: boolean;
};

const stringsDefaults = (t: TFunction): Required<Strings> => {
  return {
    headerLabel: t("greco.columnOptions"),
    booleanFilterTrueLabel: t("greco.yes"),
    booleanFilterFalseLabel: t("greco.no"),
    applyButtonLabel: t("greco.apply"),
    dateFilterOperatorIsAfterLabel: t("greco.isAfter"),
    dateFilterOperatorIsBeforeLabel: t("greco.isBefore"),
    dateFilterOperatorIsExactLabel: t("greco.isExact"),
    dateFilterOperatorIsBetweenLabel: t("greco.isBetween"),
    numberFilterOperatorGtLabel: t("numberFilterOperator.gt"),
    numberFilterOperatorLtLabel: t("numberFilterOperator.lt"),
    numberFilterOperatorEqLabel: t("numberFilterOperator.eq"),
    numberFilterOperatorBetweenLabel: t("numberFilterOperator.between"),
    numberFilterIncludeLabel: t("columnOptions.include"),
    numberFilterAndLabel: t("greco.and"),
    dragHandleTitle: t("grid.ReorderColumn"),
    visibilityCheckboxTitle: t("grid.ShowHideColumn"),
    visibilitySelectAllTitle: t("grid.SelectAll"),
    visibilityDeselectAllTitle: t("grid.DeselectAll"),
    requiredErrorMessage: t("greco.required"),
    viewExistsErrorMessage: t("greco.errors.exists", {
      object: t("greco.view"),
    }),
    viewDialogNameFieldLabel: t("greco.name"),
    viewDialogDescriptionFieldLabel: t("greco.description"),
    viewDialogFavoriteFieldLabel: t("greco.favorite"),
    viewDialogSaveButtonLabel: t("greco.save"),
    editViewDialogLabel: t("greco.editView"),
    createViewDialogLabel: t("greco.createView"),
    saveViewButtonLabel: t("greco.saveCurrentView"),
    editViewButtonLabel: t("greco.editCurrentView"),
    resetButtonLabel: t("greco.reset"),
    clearFiltersButtonLabel: t("greco.clearFilters"),
    deleteViewButtonLabel: t("greco.deleteCurrentView"),
    newViewButtonLabel: t("greco.newView"),
    clearFilterButtonLabel: t("greco.clear"),
    textFilterLabel: t("greco.containsText"),
    viewSelectNoViewsLabel: t("greco.noViews"),
    viewSelectPlaceholder: t("greco.selectView"),
    viewSelectLoadingLabel: t("greco.loading"),
    resetAllStorageTitle: t("greco.columnOptions.resetAllTitle", "Reset column settings"),
    resetAllStorageMessage: t("greco.columnOptions.resetAllMessage", "Are you sure you want to reset all column settings, filters, and saved views for this page? This cannot be undone."),
    resetAllStorageConfirm: t("greco.yes"),
    resetAllStorageCancel: t("greco.no"),
    resetAllStorageTooltip: t("greco.columnOptions.resetAllTooltip", "Reset all settings"),
    datePickerStrings: {
      months: [
        t("datePicker.January"), t("datePicker.February"), t("datePicker.March"),
        t("datePicker.April"), t("datePicker.May"), t("datePicker.June"),
        t("datePicker.July"), t("datePicker.August"), t("datePicker.September"),
        t("datePicker.October"), t("datePicker.November"), t("datePicker.December"),
      ],
      shortMonths: [
        t("datePicker.short.January"), t("datePicker.short.February"), t("datePicker.short.March"),
        t("datePicker.short.April"), t("datePicker.short.May"), t("datePicker.short.June"),
        t("datePicker.short.July"), t("datePicker.short.August"), t("datePicker.short.September"),
        t("datePicker.short.October"), t("datePicker.short.November"), t("datePicker.short.December"),
      ],
      days: [
        t("datePicker.Sunday"), t("datePicker.Monday"), t("datePicker.Tuesday"),
        t("datePicker.Wednesday"), t("datePicker.Thursday"), t("datePicker.Friday"),
        t("datePicker.Saturday"),
      ],
      shortDays: [
        t("datePicker.short.Sunday"), t("datePicker.short.Monday"), t("datePicker.short.Tuesday"),
        t("datePicker.short.Wednesday"), t("datePicker.short.Thursday"), t("datePicker.short.Friday"),
        t("datePicker.short.Saturday"),
      ],
      goToToday: t("datePicker.goToToday"),
      prevMonthAriaLabel: t("datePicker.prevMonthAriaLabel"),
      nextMonthAriaLabel: t("datePicker.nextMonthAriaLabel"),
      prevYearAriaLabel: t("datePicker.prevYearAriaLabel"),
      nextYearAriaLabel: t("datePicker.nextYearAriaLabel"),
      closeButtonAriaLabel: t("datePicker.closeButtonAriaLabel"),
    },
  };
};

export const ColumnOptionsContext = React.createContext<
  ColumnOptionsProps & { strings: Required<Strings> }
>({
  ...({}  as ColumnOptionsProps),
  columns: [],
  strings: {} as any,
});

export const ColumnOptions = (props: ColumnOptionsProps) => {
  return (
    <ColumnOptionsContext.Provider
      value={{
        ...props,
        strings: { ...stringsDefaults(props.t), ...props.strings },
        additionalOptionsHeight: props.additionalOptionsHeight ?? 80,
        additionalOptionsStyle: props.additionalOptionsStyle,
        disableButtons: props.disableButtons,
        filterProps: props.filterProps,
        usePlaceholder: props.usePlaceholder,
      }}
    >
      <ColumnOptionsPanel />
    </ColumnOptionsContext.Provider>
  );
};
